import type { ReactNode } from "react";
import { type TextStyle, View, type ViewProps, type ViewStyle } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import { BaseText } from "~/components/layout/text";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { FieldHint } from "~/components/layout/text/FieldHint";
import { Only } from "~/components/permission/Only";
import { QendoIcon, type QendoIconProps, type QendoIconSet } from "~/components/QendoIcon";
import { Flex, Margin, Width } from "~/constants/layout";
import { useTheme } from "~/providers/ThemeProvider";
import { empty } from "~/util";

export type FieldLabelProps = {
    label?: ReactNode;
    error?: string | null;
    hint?: ReactNode;
    hideLabel?: boolean;
    optional?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    labelContainerStyle?: StyleProp<ViewStyle>;
    icon?: QendoIconSet;
    iconProps?: Omit<QendoIconProps, "name">;
} & Partial<ViewProps>;

export type PassthroughFieldLabelProps = Pick<FieldLabelProps, "label" | "error" | "hideLabel" | "optional">;

export const FieldLabel = ({
    label = "",
    error,
    hideLabel,
    optional,
    children,
    labelStyle,
    hint,
    labelContainerStyle,
    icon,
    iconProps,
    ...viewProps
}: FieldLabelProps) => {
    const shouldHide = hideLabel || (empty(label) && !optional && !icon);
    const { colours } = useTheme();
    return (
        <View {...viewProps} style={[Flex.start, viewProps.style]} accessible={true}>
            <Only not when={shouldHide}>
                <View style={[Margin.bottom_xs, Width.full, Flex.start, labelContainerStyle]}>
                    <BaseText size="md" style={labelStyle}>
                        <Only
                            when={icon}
                            show={() => (
                                <QendoIcon
                                    {...iconProps}
                                    name={icon as QendoIconSet}
                                    size={iconProps?.size ?? 24}
                                    color={iconProps?.color ?? colours.icon.secondary}
                                />
                            )}
                        />
                        {label}
                        <Only when={optional}>
                            <BaseText size="md" colour="secondary">
                                {" "}
                                (optional)
                            </BaseText>
                        </Only>
                    </BaseText>
                </View>
            </Only>
            {children}
            <Only when={hint}>
                <FieldHint>{hint}</FieldHint>
            </Only>
            <Only when={error}>
                <ErrorMessage>{error}</ErrorMessage>
            </Only>
        </View>
    );
};
